/**
 * Does this part fit that machine? - the one answer, for everything that asks it.
 *
 * Three places on the store ask the same question of the same data: the product finder, the
 * "parts for your machine" results page, and the badge on a product page. Shop-by-machine asks a
 * smaller version of it for the badge on a collection card. They used to carry their own copies
 * of the matching, which drifted: a fix to one left the others answering differently about the
 * same part, and a shopper met both answers in one visit without anything explaining why. Twice
 * that cost a supplier's entire catalogue on a machine - see normalizeModel below.
 *
 * So this file holds the matching, and each caller keeps only what is its own: which products to
 * fetch, and how to word the answer. Loaded before them all with `defer`, which runs scripts in
 * the order the page lists them.
 *
 * Nothing here ever says a part does NOT fit. The catalogue records what a manufacturer
 * published, and silence in it is a gap in the record rather than a statement of incompatibility.
 * A shopper turned away from a part that would have fitted is the worse mistake.
 */
(function () {
  "use strict";

  /**
   * Words that mark a version of a machine rather than the machine itself. They never decide
   * which machine it is, but they do decide how sure the answer can be: a part listed only for
   * the TRV is not established for the XT.
   */
  var TRIM_TOKENS = {};
  ["XT", "EPS", "LTD", "SE", "LE", "GT", "EFI", "EFT", "FIS", "CORE", "TRV", "TBX", "XC", "MUD",
   "PRO", "MUDPRO", "AUTO", "AUTOMATIC", "UTILITY", "CREW", "H1", "H2", "4X4", "2X4", "4WD",
   "2WD", "I", "W"].forEach(function (t) { TRIM_TOKENS[t] = true; });

  /** Pure noise: catch-all markers and colourways, ignored everywhere. */
  var NOISE_TOKENS = { ALL: true, RED: true, GREEN: true };

  /**
   * A machine name with its punctuation taken out, so two spellings of one machine can be
   * compared.
   *
   * fitment-machines.json is built with the punctuation stripped, because that is the only way
   * three catalogues that disagree about a hyphen can describe one machine. The fitment records
   * on each product keep it. So the file says EXPEDITION SE 600R E TEC and the product says
   * EXPEDITION SE 600R E-TEC, and compared as they stand those are two different machines.
   *
   * Read wrong, that drops every part from whichever supplier writes the hyphen, without a word:
   * a 2018 Skandic XU 600 ACE lost all 32 of its Kimpex parts, and a 2025 Expedition 600 lost 42
   * of 138. Both looked from the outside like a supplier the store simply had nothing from.
   */
  function normalizeModel(name) {
    return String(name || "").toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim();
  }

  /**
   * A machine name split into the parts that decide identity.
   *
   *   core    the first number in it - the engine size, mostly, and the strongest signal there is
   *   tokens  the identifying words, with their digits dropped so DL800 gives DL and 500I gives I
   *   trims   the version words above, kept apart because they weaken a match without breaking it
   *
   * Punctuation splits words rather than joining them, so E-TEC gives E and TEC - the same two
   * words the machine file spells apart, and the two sides line up wherever the name came from.
   */
  function parseModel(name) {
    var clean = normalizeModel(name);
    var match = clean.match(/\d+/);
    var tokens = [];
    var trims = [];
    clean.split(/\s+/).forEach(function (raw) {
      var t = raw.replace(/[^A-Z0-9]/g, "");
      if (!t || NOISE_TOKENS[t]) return;
      if (TRIM_TOKENS[t]) { trims.push(t); return; }
      var letters = t.replace(/\d+/g, "");
      if (!letters || NOISE_TOKENS[letters]) return;
      if (TRIM_TOKENS[letters]) { trims.push(letters); return; }
      tokens.push(letters);
    });
    return { core: match ? match[0] : null, tokens: tokens, trims: trims };
  }

  function subset(a, b) {
    return a.every(function (t) { return b.indexOf(t) !== -1; });
  }

  /**
   * How sure a part found under `source` is for a shopper who picked `selected`:
   *
   *   exact    the same machine, however either side spells it
   *   family   a listing that covers theirs - SPORTSMAN 850, 500 (ALL) and 500 XT all cover an
   *            ALTERRA 500 XT EPS, because the publisher's own listing includes their machine
   *   similar  a neighbour or another version - ALTERRA 500 TRV, HDX 500 XT. Probably fits;
   *            nothing here establishes it, so it is never claimed as a fit.
   */
  function classifyMatch(selected, source) {
    if (!source) return "similar";
    if (normalizeModel(source) === normalizeModel(selected)) return "exact";
    var sel = parseModel(selected);
    var src = parseModel(source);
    // A different engine size is a different machine, whatever else the words share.
    if (sel.core && src.core && sel.core !== src.core) return "similar";
    return subset(src.tokens, sel.tokens) && subset(src.trims, sel.trims) ? "family" : "similar";
  }

  /**
   * What a spelling carries beyond the machine: the model with the machine's own words struck
   * out, one occurrence each, everything else left where it stands.
   *
   * Struck out rather than sliced off the front, because the catalogue writes one machine both
   * ways round - ALTERRA 550 TRV XT and 550 ALTERRA TRV XT. A slice only works on one of them,
   * and the failure is not cosmetic: an unread version looks like "no version", which is a badge
   * saying a part fits the whole machine when it was only established for one of them.
   *
   * Mirrors trimAgainst in Syncshop's src/services/fitment-trims.ts, which builds the groups.
   */
  function trimAgainst(base, model) {
    var remaining = String(base).toUpperCase().replace(/\([^)]*\)/g, " ")
      .replace(/[^A-Z0-9]+/g, " ").trim().split(/\s+/).filter(Boolean);
    var kept = [];
    String(model).toUpperCase().split(/\s+/).filter(Boolean).forEach(function (part) {
      var word = part.replace(/[^A-Z0-9]/g, "");
      var at = word ? remaining.indexOf(word) : -1;
      if (at !== -1 && part === word) { remaining.splice(at, 1); return; }
      /**
       * The machine's words written as one - KINGQUAD for KING QUAD, the way Kimpex spells it.
       * Left in, "750 KINGQUAD" read as a KINGQUAD version of the King Quad 750 and the badge
       * said "Fits KINGQUAD only" on a part filed under the machine itself.
       */
      if (part === word) {
        for (var i = 0; i < remaining.length; i++) {
          for (var n = 2; n <= 3 && i + n <= remaining.length; n++) {
            if (remaining.slice(i, i + n).join("") === word) { remaining.splice(i, n); return; }
          }
        }
      }
      kept.push(part);
    });
    return kept.join(" ").trim();
  }

  /**
   * Gamma qualifies a machine in brackets - (EFI), (4T), (EBS), (48"), (DIESEL) - and those are
   * real differences between machines, not decoration, so the words stay. Only the brackets go,
   * which is what made the badge read like a database row.
   *
   * A note carrying a comma keeps its brackets: "(EXC TOURING, HIGH LIFTER AND 55")" is an aside
   * with commas of its own, and unbracketed it would run into the comma-separated list beside it.
   * The trailing full stop on "(OPT.)" goes with the brackets.
   */
  function readableTrim(name) {
    return String(name).replace(/\(([^),]{1,20})\)/g, "$1").replace(/\.$/, "")
      .replace(/\s+/g, " ").trim();
  }

  /**
   * Ranges merged only where they touch. A gap is real information - the part does not fit the
   * years in between - and closing it would invent fitment.
   */
  function mergeRanges(ranges) {
    var sorted = ranges.slice().sort(function (a, b) { return a[0] - b[0] || a[1] - b[1]; });
    var merged = [];
    sorted.forEach(function (r) {
      var last = merged[merged.length - 1];
      if (last && r[0] <= last[1] + 1) last[1] = Math.max(last[1], r[1]);
      else merged.push([r[0], r[1]]);
    });
    return merged;
  }

  /** "2015-2019, 2021", merged first so the same years are never shown twice. */
  function formatRanges(ranges) {
    return mergeRanges(ranges).map(function (r) {
      return r[0] === r[1] ? String(r[0]) : r[0] + "-" + r[1];
    }).join(", ");
  }

  /**
   * Settles one product against the machine and year a shopper chose.
   *
   *   fits           a machine covering theirs publishes a year range that includes their year
   *   no-year        that machine is covered and the catalogue publishes no model year for it -
   *                  outdoor equipment is not sold by year, so this is an answer, not a gap
   *   trim           a different version of THEIR machine covers their year - shown, with the
   *                  badge naming that version so they can check it against their own
   *   check          nothing readable to check - shown and labelled, never claimed as a fit
   *   wrong-year     the machine is covered and none of its published years include theirs
   *   other-machine  the part names this make, but only machines that are not theirs
   *   range          the part is listed only for their machine's range - SPORTSMAN, with no
   *                  engine - and is an accessory, so it is offered with a "check your model".
   *                  `range` on the result is the range's name. A PART listed that way is never
   *                  this or any fit: the listing says nothing about which Sportsman it is for.
   *
   * The last two hide a part, and both are drawn from data complete enough to justify it: the
   * part's own fitment says which machines and years it covers, and theirs is not among them.
   *
   * A neighbouring machine does not rescue a part from either. An Alterra 500 owner was being
   * shown parts established for their machine in 2017-2018 but not 2020, on the strength of the
   * same part fitting an Alterra 450 in 2020 - a different machine answering their year for them.
   *
   * `members` is every spelling of the machine they picked, from the groups in
   * fitment-machines.json. A spelling in that list IS their machine whatever the tier says:
   * classifyMatch cannot tell a version of it from a neighbour, and dropping the versions would
   * take away the very parts the version badge exists to show.
   *
   * Returns the verdict, the tier behind it, the spellings that answered, and every year range
   * those spellings publish - the last so a caller can say which years a part does cover.
   *
   * With `requireYear`, a search with no year is "nothing to check" rather than a fit; without
   * it, no year means the year is not in question and the machine alone settles it.
   */
  function yearVerdict(fitmentJson, selMake, selModel, selYear, members, options) {
    var make = String(selMake || "").toUpperCase().trim();
    var year = parseInt(selYear, 10) || null;
    var requireYear = Boolean(options && options.requireYear);
    var out = { verdict: "check", tier: "similar", matched: [], years: [] };
    if (!make || (requireYear && !year)) return out;

    var RANK = { exact: 3, family: 2, similar: 1 };
    var best = null;         // a covering machine whose years include theirs
    var noYear = null;       // a covering machine the catalogue publishes no year for
    var sawCovering = false; // their machine is named, whatever years it carries
    var sawMachine = false;  // this make appears at all, so a year check means something
    var trimHit = false;     // another version of their machine covers their year
    var rangeHit = null;     // an accessory listed for their machine's range, in their year
    var ownNames = (members || []).map(normalizeModel);

    (fitmentJson || []).forEach(function (raw) {
      var records = raw;
      if (typeof raw === "string") {
        try { records = JSON.parse(raw); } catch (e) { return; }
      }
      if (!Array.isArray(records)) return;

      records.forEach(function (rec) {
        if (String(rec.make || "").toUpperCase().trim() !== make) return;
        var spelled = normalizeModel(rec.model);
        var tier = classifyMatch(selModel, spelled);
        var own = ownNames.indexOf(spelled) !== -1;
        sawMachine = true;
        /**
         * A listing for the range, not a machine: read as covering theirs it told a 570 owner a
         * brake pad listed for "SPORTSMAN" fitted. The build flags these (SyncShop's
         * fitment-range.ts); an accessory is offered with a check, a part is not offered at all.
         */
        if (rec.range) {
          if (tier === "similar" || rec.range !== "accessory") return;
          var within = (Array.isArray(rec.years) ? rec.years : []).filter(function (r) {
            return Array.isArray(r) && r[0];
          });
          within.forEach(function (r) { out.years.push([r[0], r[1] || r[0]]); });
          if (!within.length || !year || within.some(function (r) { return year >= r[0] && year <= (r[1] || r[0]); })) {
            rangeHit = rangeHit || spelled;
          } else {
            sawCovering = true;
          }
          return;
        }
        // Another machine of this make. Nothing about theirs can be read from it.
        if (tier === "similar" && !own) return;
        sawCovering = true;

        // [0, 0] is the catalogue saying this machine has no model year, not year zero.
        var ranges = (Array.isArray(rec.years) ? rec.years : []).filter(function (r) {
          return Array.isArray(r) && r[0];
        });
        if (!ranges.length) {
          out.matched.push(spelled);
          if (tier !== "similar" && (!noYear || RANK[tier] > RANK[noYear])) noYear = tier;
          return;
        }
        ranges.forEach(function (r) { out.years.push([r[0], r[1] || r[0]]); });
        if (year && !ranges.some(function (r) { return year >= r[0] && year <= (r[1] || r[0]); })) return;
        out.matched.push(spelled);
        if (tier === "similar") { trimHit = true; return; }
        if (!best || RANK[tier] > RANK[best]) best = tier;
      });
    });

    if (best) { out.verdict = "fits"; out.tier = best; }
    else if (noYear) { out.verdict = "no-year"; out.tier = noYear; }
    // Their machine, another version of it, in their year. Shown, and the badge says which.
    else if (trimHit) out.verdict = "trim";
    // Listed for the range only, and an accessory: offered, with the range named on the badge.
    else if (rangeHit) { out.verdict = "range"; out.range = rangeHit; }
    // Their machine is in the list, their year is not: worth telling them about, since the part
    // was there last season and its absence otherwise looks like a mistake.
    else if (sawCovering) out.verdict = "wrong-year";
    // Only other machines of this make. Nothing to say and nothing to count.
    else if (sawMachine) out.verdict = "other-machine";
    return out;
  }

  /**
   * The versions of a machine a part was established for, when the shopper picked the whole
   * machine and the part covers only some of it. Null when there is nothing to raise.
   *
   * Two of Gamma's listings are the machine itself rather than a version: the plain name, and the
   * catch-all it writes as "(ALL)". A part filed under either is filed against the whole machine,
   * so it gets no warning - measured on a 2019 Sportsman 850, warning on those put a "check your
   * version" on 40 of 185 parts that did not need one, and a badge that cries wolf is a badge
   * shoppers stop reading.
   *
   * `picked` is the spelling they actually chose, where they chose one. A part filed for the very
   * machine they picked has nothing for them to check, whatever else it covers.
   */
  function matchedTrims(base, members, matched, picked) {
    if (!members || !matched || !matched.length) return null;
    var hit = members.filter(function (m) { return matched.indexOf(normalizeModel(m)) !== -1; });
    if (!hit.length || hit.length === members.length) return null;
    if (picked && hit.indexOf(picked) !== -1) return null;
    var names = hit.map(function (m) { return trimAgainst(base, m); });
    // Both spellings of the catch-all: the catalogue writes "(ALL)" and "ALL".
    if (names.some(function (n) { return n === "" || n.toUpperCase() === "ALL" || n === "(ALL)"; })) return null;
    var shown = names.map(readableTrim).filter(Boolean);
    return shown.length ? shown : null;
  }

  /** "SP, HIGH LIFTER EPS and 2 more" - a list of versions, cut short before it stops being read. */
  function trimList(names, limit) {
    var max = limit || 3;
    var text = names.slice(0, max).join(", ");
    if (names.length > max) text += " and " + (names.length - max) + " more";
    return text;
  }

  window.GHSFitment = {
    normalizeModel: normalizeModel,
    parseModel: parseModel,
    classifyMatch: classifyMatch,
    trimAgainst: trimAgainst,
    readableTrim: readableTrim,
    mergeRanges: mergeRanges,
    formatRanges: formatRanges,
    yearVerdict: yearVerdict,
    matchedTrims: matchedTrims,
    trimList: trimList
  };
})();
